const resources = {
  en: {
    billingHistory: {
      title: 'Billing history',
      description: 'Review your current subscription state, recent billing periods, and payment history without leaving the app.',
      actions: {
        refresh: 'Refresh',
        managePortal: 'Open billing portal',
        reconcile: 'Refresh billing status',
        reconcileSuccess: 'Billing status refreshed',
        reconcileError: 'Failed to refresh billing status',
      },
      alerts: {
        partial: 'Payment history is partially unavailable right now. Your current subscription state is still shown from canonical backend truth.',
        loadError: 'Failed to load billing history.',
        renewalFailureTitle: 'Payment issue needs recovery',
        renewalFailureDescription: 'Your current subscription status is {{status}}. Update the payment method in the billing portal, then refresh billing status here.',
        recoveredTitle: 'Recent billing recovery detected',
        recoveredDescription: 'A later successful payment was recorded after a previous renewal issue.',
        latestFailureAt: 'Latest failed attempt: {{value}}',
        latestRecoveryAt: 'Latest recovery: {{value}}',
      },
      summary: {
        title: 'Subscription snapshot',
        caption: 'Your current billing state comes from backend-owned subscription truth.',
        plan: 'Plan',
        status: 'Status',
        currentPeriod: 'Current period',
        autoRenew: 'Auto-renew',
        cancelAt: 'Cancel date',
        payments: 'Payment entries',
        available: 'Available',
        scheduledToEnd: 'Scheduled to end',
        off: 'Off',
      },
      details: {
        title: 'Current billing state',
        description: 'Use this view to confirm the active plan, access window, and whether cancellation is already scheduled.',
        currentPeriodStart: 'Current period start',
        currentPeriodEnd: 'Current period end',
        cancelAtPeriodEnd: 'Cancel at period end',
        cancelAt: 'Cancel date',
        endedAt: 'Ended at',
        lastPlan: 'Last paid plan',
      },
      history: {
        title: 'Payment history',
        description: 'Invoices and payments are normalized server-side so you can inspect billing without raw Stripe terminology.',
        empty: 'No payment history is available yet.',
        source: 'Source',
        plan: 'Plan',
        status: 'Status',
        amount: 'Amount',
        details: 'Details',
        createdAt: 'Created',
        paidAt: 'Paid',
        period: 'Billing period',
        links: 'Links',
        invoice: 'Invoice',
        receipt: 'Receipt',
        pdf: 'PDF',
      },
      activity: {
        title: 'Billing activity',
        description: 'A clear timeline of payment attempts and subscription access changes.',
        empty: 'No billing activity is available yet.',
        meta: {
          invoiceNumber: 'Invoice: {{value}}',
          providerStatus: 'Provider status: {{value}}',
          reason: 'Reason: {{value}}',
          subscriptionId: 'Subscription: {{value}}',
        },
        plan: {
          accessChange: 'Access change',
          changedTitle: 'Plan changed',
          downgradedTitle: 'Subscription access changed',
          changedDescription: 'Your access changed from {{from}} to {{to}}.',
        },
        payment: {
          paid: {
            title: 'Payment succeeded',
            description: '{{amount}} was paid for {{plan}}. Billing period: {{period}}.',
          },
          failed: {
            title: 'Payment failed',
            description: 'Stripe could not collect {{amount}} for {{plan}}. Billing period: {{period}}.',
          },
          open: {
            title: 'Invoice open',
            description: '{{amount}} is still open for {{plan}}. Billing period: {{period}}.',
          },
          processing: {
            title: 'Payment processing',
            description: '{{amount}} is being processed for {{plan}}. Billing period: {{period}}.',
          },
          requires_action: {
            title: 'Payment needs action',
            description: '{{amount}} needs confirmation or a payment-method update for {{plan}}. Billing period: {{period}}.',
          },
          refunded: {
            title: 'Payment refunded',
            description: '{{amount}} was refunded for {{plan}}. Billing period: {{period}}.',
          },
          canceled: {
            title: 'Payment canceled',
            description: '{{amount}} was canceled for {{plan}}. Billing period: {{period}}.',
          },
          void: {
            title: 'Invoice voided',
            description: '{{amount}} was voided for {{plan}}. Billing period: {{period}}.',
          },
          partially_paid: {
            title: 'Invoice partially paid',
            description: '{{amount}} was partially paid for {{plan}}. Billing period: {{period}}.',
          },
          unknown: {
            title: 'Billing event',
            description: 'A billing event was recorded for {{plan}}. Billing period: {{period}}.',
          },
        },
        lifecycle: {
          worker_set_grace: {
            title: 'Grace period started',
            description: 'Access changed from {{fromStatus}} to {{toStatus}} after the local billing period ended on {{periodEnd}}.',
          },
          worker_set_expired: {
            title: 'Subscription expired',
            description: 'Access changed from {{fromStatus}} to {{toStatus}} after the grace window ended.',
          },
          worker_pre_grace_provider_recovered: {
            title: 'Billing status recovered',
            description: 'Stripe showed an active subscription before grace was applied. Access stayed {{toStatus}} through {{periodEnd}}.',
          },
          provider_subscription_applied: {
            title: 'Subscription synced',
            description: 'Provider state updated access from {{fromStatus}} to {{toStatus}} for {{toPlan}}. Current period ends {{periodEnd}}.',
          },
          unknown: {
            title: 'Subscription event',
            description: 'A subscription lifecycle event was recorded. Current status: {{toStatus}}.',
          },
        },
      },
      statuses: {
        active: 'Active',
        inactive: 'Inactive',
        grace: 'Grace period',
        expired: 'Expired',
        paid: 'Paid',
        refunded: 'Refunded',
        open: 'Open',
        failed: 'Failed',
        processing: 'Processing',
        draft: 'Draft',
        void: 'Voided',
        canceled: 'Canceled',
        partially_paid: 'Partially paid',
        requires_action: 'Action required',
        unknown: 'Unknown',
      },
      labels: {
        yes: 'Yes',
        no: 'No',
        notAvailable: 'Not available',
        freePlan: 'Free',
        oneTimePayment: 'One-time payment',
        recurringInvoice: 'Recurring invoice',
      },
    },
  },
  uk: {
    billingHistory: {
      title: 'Історія оплат',
      description: 'Переглядайте поточний стан підписки, останні білінгові періоди та історію оплат без переходу в зовнішні сервіси.',
      actions: {
        refresh: 'Оновити',
        managePortal: 'Відкрити білінг-портал',
        reconcile: 'Оновити білінговий статус',
        reconcileSuccess: 'Білінговий статус оновлено',
        reconcileError: 'Не вдалося оновити білінговий статус',
      },
      alerts: {
        partial: 'Історія оплат зараз доступна частково. Поточний стан підписки все одно показано з канонічної backend-правди.',
        loadError: 'Не вдалося завантажити історію оплат.',
        renewalFailureTitle: 'Проблема з оплатою потребує відновлення',
        renewalFailureDescription: 'Поточний статус підписки — {{status}}. Оновіть спосіб оплати в білінг-порталі, а потім оновіть статус тут.',
        recoveredTitle: 'Зафіксовано недавнє відновлення білінгу',
        recoveredDescription: 'Після попередньої проблеми renewal було зафіксовано успішну оплату.',
        latestFailureAt: 'Остання невдала спроба: {{value}}',
        latestRecoveryAt: 'Останнє відновлення: {{value}}',
      },
      summary: {
        title: 'Знімок підписки',
        caption: 'Поточний білінговий стан походить із backend-owned subscription truth.',
        plan: 'План',
        status: 'Статус',
        currentPeriod: 'Поточний період',
        autoRenew: 'Автоподовження',
        cancelAt: 'Дата завершення',
        payments: 'Записи оплат',
        available: 'Доступно',
        scheduledToEnd: 'Заплановано завершення',
        off: 'Вимкнено',
      },
      details: {
        title: 'Поточний білінговий стан',
        description: 'Тут можна перевірити активний план, вікно доступу та чи вже заплановане завершення.',
        currentPeriodStart: 'Початок поточного періоду',
        currentPeriodEnd: 'Кінець поточного періоду',
        cancelAtPeriodEnd: 'Завершити наприкінці періоду',
        cancelAt: 'Дата завершення',
        endedAt: 'Завершено',
        lastPlan: 'Останній платний план',
      },
      history: {
        title: 'Історія оплат',
        description: 'Інвойси та оплати нормалізуються на сервері, тому білінг можна перевірити без сирої Stripe-термінології.',
        empty: 'Історія оплат поки недоступна.',
        source: 'Джерело',
        plan: 'План',
        status: 'Статус',
        amount: 'Сума',
        details: 'Деталі',
        createdAt: 'Створено',
        paidAt: 'Оплачено',
        period: 'Білінговий період',
        links: 'Посилання',
        invoice: 'Інвойс',
        receipt: 'Квитанція',
        pdf: 'PDF',
      },
      activity: {
        title: 'Білінгова активність',
        description: 'Зрозуміла хронологія спроб оплат і змін доступу до підписки.',
        empty: 'Білінгова активність поки недоступна.',
        meta: {
          invoiceNumber: 'Інвойс: {{value}}',
          providerStatus: 'Статус провайдера: {{value}}',
          reason: 'Причина: {{value}}',
          subscriptionId: 'Підписка: {{value}}',
        },
        plan: {
          accessChange: 'Зміна доступу',
          changedTitle: 'План змінено',
          downgradedTitle: 'Доступ до підписки змінено',
          changedDescription: 'Ваш доступ змінено з {{from}} на {{to}}.',
        },
        payment: {
          paid: {
            title: 'Оплата успішна',
            description: '{{amount}} сплачено за {{plan}}. Білінговий період: {{period}}.',
          },
          failed: {
            title: 'Оплата не пройшла',
            description: 'Stripe не зміг списати {{amount}} за {{plan}}. Білінговий період: {{period}}.',
          },
          open: {
            title: 'Інвойс відкритий',
            description: '{{amount}} ще відкрито для {{plan}}. Білінговий період: {{period}}.',
          },
          processing: {
            title: 'Оплата обробляється',
            description: '{{amount}} обробляється для {{plan}}. Білінговий період: {{period}}.',
          },
          requires_action: {
            title: 'Оплата потребує дії',
            description: '{{amount}} потребує підтвердження або оновлення способу оплати для {{plan}}. Білінговий період: {{period}}.',
          },
          refunded: {
            title: 'Оплату повернено',
            description: '{{amount}} повернено за {{plan}}. Білінговий період: {{period}}.',
          },
          canceled: {
            title: 'Оплату скасовано',
            description: '{{amount}} скасовано для {{plan}}. Білінговий період: {{period}}.',
          },
          void: {
            title: 'Інвойс анульовано',
            description: '{{amount}} анульовано для {{plan}}. Білінговий період: {{period}}.',
          },
          partially_paid: {
            title: 'Інвойс частково оплачено',
            description: '{{amount}} частково сплачено за {{plan}}. Білінговий період: {{period}}.',
          },
          unknown: {
            title: 'Білінгова подія',
            description: 'Зафіксовано білінгову подію для {{plan}}. Білінговий період: {{period}}.',
          },
        },
        lifecycle: {
          worker_set_grace: {
            title: 'Почався grace-період',
            description: 'Доступ змінено з {{fromStatus}} на {{toStatus}} після завершення локального білінгового періоду {{periodEnd}}.',
          },
          worker_set_expired: {
            title: 'Підписка завершилась',
            description: 'Доступ змінено з {{fromStatus}} на {{toStatus}} після завершення grace-вікна.',
          },
          worker_pre_grace_provider_recovered: {
            title: 'Білінговий статус відновлено',
            description: 'Stripe показав активну підписку до застосування grace. Доступ залишився {{toStatus}} до {{periodEnd}}.',
          },
          provider_subscription_applied: {
            title: 'Підписку синхронізовано',
            description: 'Стан провайдера оновив доступ з {{fromStatus}} на {{toStatus}} для {{toPlan}}. Поточний період завершується {{periodEnd}}.',
          },
          unknown: {
            title: 'Подія підписки',
            description: 'Зафіксовано lifecycle-подію підписки. Поточний статус: {{toStatus}}.',
          },
        },
      },
      statuses: {
        active: 'Активний',
        inactive: 'Неактивний',
        grace: 'Пільговий період',
        expired: 'Завершений',
        paid: 'Оплачено',
        refunded: 'Повернено',
        open: 'Відкрито',
        failed: 'Помилка оплати',
        processing: 'Обробляється',
        draft: 'Чернетка',
        void: 'Анульовано',
        canceled: 'Скасовано',
        partially_paid: 'Частково оплачено',
        requires_action: 'Потрібна дія',
        unknown: 'Невідомо',
      },
      labels: {
        yes: 'Так',
        no: 'Ні',
        notAvailable: 'Недоступно',
        freePlan: 'Безкоштовний',
        oneTimePayment: 'Разова оплата',
        recurringInvoice: 'Періодичний інвойс',
      },
    },
  },
}

export default resources
